import { useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Floating support chat bubble (bottom-right). Opens a tiny form that submits
 * to /api/contact. Persists nothing locally except the open state.
 */
export function ChatSupportWidget() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !message) {
      toast.error("Email and message required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name || "Anonymous",
        email,
        subject: "Live chat from widget",
        category: "support",
        message,
      });
      setSent(true);
      setMessage("");
      toast.success("Sent — we'll reply by email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => { setOpen(!open); if (!open) setSent(false); }}
        className="fixed bottom-[calc(7.5rem+env(safe-area-inset-bottom))] right-3 z-[80] grid h-11 w-11 place-items-center rounded-full bg-foreground/90 text-background shadow-[0_8px_30px_-4px_rgba(0,0,0,0.5)] backdrop-blur transition hover:scale-105 sm:bottom-5 sm:right-5 sm:h-14 sm:w-14"
        data-testid="chat-support-toggle"
        aria-label="Open support chat"
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
      </button>

      {open && (
        <div
          className="fixed bottom-[calc(10.5rem+env(safe-area-inset-bottom))] right-3 z-[80] w-[calc(100vw-1.5rem)] max-w-[340px] overflow-hidden rounded-2xl border border-border bg-card shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] sm:bottom-24 sm:right-5 sm:w-[340px]"
          data-testid="chat-support-panel"
        >
          <div className="border-b border-border/60 bg-secondary/50 px-4 py-3">
            <div className="text-sm font-semibold">Sabzy support</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              Avg reply &lt; 6h · we read every message
            </div>
          </div>

          {sent ? (
            <div className="p-5 text-center text-sm" data-testid="chat-support-sent">
              <div className="text-base font-semibold">Sent ✓</div>
              <p className="mt-1.5 text-muted-foreground">
                We'll reply to <b>{email}</b> shortly.
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setSent(false)}>
                Send another
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3 p-4" data-testid="chat-support-form">
              <Input
                placeholder="Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-9 text-sm"
                data-testid="chat-support-name"
              />
              <Input
                type="email"
                required
                placeholder="Email *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-9 text-sm"
                data-testid="chat-support-email"
              />
              <Textarea
                required
                rows={4}
                placeholder="How can we help?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="resize-none text-sm"
                data-testid="chat-support-message"
              />
              <Button type="submit" disabled={loading} className="w-full" data-testid="chat-support-submit">
                {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                Send
              </Button>
            </form>
          )}
        </div>
      )}
    </>
  );
}
